import React from 'react';
import AppActionMenu from '../../../../components/AppActionMenu';

export default function DailyAttendanceTable({
  records = [],
  isLoading = false,
  onDelete,
  onResetFilters,
}) {
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Daily Attendance...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">published_with_changes</span>
          </div>
          <h3 className="text-lg font-bold text-on-surface">No entries meet selected criteria</h3>
          <p className="text-xs text-on-surface-variant max-w-md">
            Either there are no entries in the selected module or you have selected invalid filter conditions.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  const getAttendanceBadgeClass = (status) => {
    switch (status) {
      case 'P':
      case 'Present':
        return 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20';
      case 'L':
      case 'Late In':
        return 'bg-amber-500/10 text-amber-600 border border-amber-500/20';
      case 'EG':
      case 'Early Out':
        return 'bg-blue-500/10 text-blue-600 border border-blue-500/20';
      case 'HF':
      case 'Half Day':
        return 'bg-orange-500/10 text-orange-600 border border-orange-500/20';
      case 'LH':
      case 'Leave Half Day':
        return 'bg-cyan-500/10 text-cyan-600 border border-cyan-500/20';
      case 'A':
      case 'Absent':
        return 'bg-rose-500/10 text-rose-600 border border-rose-500/20';
      case 'WO':
      case 'Weekly Off':
        return 'bg-purple-500/10 text-purple-600 border border-purple-500/20';
      case 'HL':
      case 'Holiday':
        return 'bg-indigo-500/10 text-indigo-600 border border-indigo-500/20';
      case 'CO':
      case 'Comp Off':
        return 'bg-teal-500/10 text-teal-600 border border-teal-500/20';
      default:
        return 'bg-sky-500/10 text-sky-600 border border-sky-500/20';
    }
  };

  const getAttendanceLabel = (status) => {
    switch (status) {
      case 'P': return 'Present';
      case 'L': return 'Late In';
      case 'EG': return 'Early Out';
      case 'HF': return 'Half Day';
      case 'LH': return 'Leave Half';
      case 'A': return 'Absent';
      case 'WO': return 'Weekly Off';
      case 'HL': return 'Holiday';
      case 'CO': return 'Comp Off';
      default: return status;
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Emp Code</th>
              <th className="px-5 py-3.5">Name</th>
              <th className="px-4 py-3.5">Date</th>
              <th className="px-4 py-3.5 text-center">First Swipe</th>
              <th className="px-4 py-3.5 text-center">Last Swipe</th>
              <th className="px-4 py-3.5 text-center">Working Hours</th>
              <th className="px-5 py-3.5 text-center">Attendance</th>
              <th className="w-20 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item) => (
              <tr
                key={item.id}
                className="transition-colors hover:bg-surface-container-low/50 group"
              >
                {/* EmpID */}
                <td className="px-5 py-3.5 text-xs font-mono font-bold text-on-surface-variant">
                  {item.empId}
                </td>

                {/* Name */}
                <td className="px-5 py-3.5 font-bold text-on-surface group-hover:text-primary transition-colors">
                  <div className="flex flex-col">
                    <span>{item.name}</span>
                    <span className="text-[11px] font-normal text-on-surface-variant">
                      {item.department} &bull; {item.branch}
                    </span>
                  </div>
                </td>

                {/* Date */}
                <td className="px-4 py-3.5 text-xs font-medium text-on-surface whitespace-nowrap">
                  {item.dateDisplay || item.date}
                </td>

                {/* First Swipe */}
                <td className="px-4 py-3.5 text-center text-xs font-mono font-medium text-on-surface">
                  {item.firstSwipe}
                </td>

                {/* Last Swipe */}
                <td className="px-4 py-3.5 text-center text-xs font-mono font-medium text-on-surface">
                  {item.lastSwipe}
                </td>

                {/* Total Working Hours */}
                <td className="px-4 py-3.5 text-center text-xs font-mono font-medium text-on-surface-variant">
                  {item.totalHours}
                </td>

                {/* Attendance */}
                <td className="px-5 py-3.5 text-center">
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-0.5 text-xs font-bold ${getAttendanceBadgeClass(
                      item.attendance
                    )}`}
                  >
                    {getAttendanceLabel(item.attendance)}
                  </span>
                </td>

                {/* Actions */}
                <td
                  className="px-5 py-3.5 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                    items={[
                      { key: 'delete', label: 'Delete Record', icon: 'delete' },
                    ]}
                    onSelect={(key) => {
                      if (key === 'delete') {
                        onDelete?.(item.id, item.name);
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
